import { FormattedMessage } from "react-intl";
import { Button } from "../Button";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Modal } from "../Modal";
import { H3, Span, Text } from "../Text";
import { Card, CardBody, CardFooter } from "../Card";
import { Box } from "../Box";
import { Website } from "src/orchd-client";
import { Icon } from "../Icon";
import { ProgressBar } from "../ProgressBar";
import { DoneAnimation } from "./DoneAnimation";
import { backupsApi } from "src/api_services/backups/service";
import { style } from './UploadSystem.style'

interface Props {
  uploadUrl: string;
  website: Website
  setModalOpen: (bool) => void
}

export enum UploadState {
  Processing,
  Succeeded,
  Failed
}

export const UploadSystem = ({ uploadUrl, website, setModalOpen }: Props) => {
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadState,setUploadState] = useState(UploadState.Processing);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [logData, setLogData] = useState("");
  const logRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      backupsApi.getWebsiteRestoreLog({ websiteId: website.id })  // Replace with your URL
        .then((res) => res.data)
        .then((newData) => {
          setLogData((prevLog) => {
            if(!newData || newData.length === 0) {
              return prevLog;
            }
            return prevLog + (prevLog ? `\n` : '') + newData.join('\n')
          });
        })
        .catch((err) => {
          console.error('Polling error:', err);
        });
    }, 500); // Poll every 1000 ms = 1 second

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [website]);

  useEffect(() => {
    if (logRef.current?.scrollTo) {
      logRef.current.scrollTo(0, logRef.current.scrollHeight);
    }
  }, [logData]);



  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setUploadProgress(0); // reset progress on new file
    handleUpload(file);
  };

  const handleUpload = async (file: File) => {
    console.log("Handling upload")
    setUploading(true);
    setLogData(`Beginning upload of ${file.name}`)
    try {
      const response = await axios.post(uploadUrl, file, {
        headers: { "Content-Type": "application/octet-stream", },
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(percent);
        },
      });
      setUploadState(UploadState.Succeeded);
      console.log('Upload complete:', response.data);
    } catch (err) {
      setUploadState(UploadState.Failed)
      setLogData(prevLog => prevLog + (prevLog ? `\n` : '') + `Upload failed, check ${file.name} is a valid Enhance backup`);
      console.error('Upload failed:', err);
    } finally {

    }
  };





  const fileName = selectedFile?.name || 'Unknown';

  if(uploadState && uploadState === UploadState.Succeeded) {
    return (
      <Modal>
        <Card>
          <CardBody>
            <Box position="relative" h="100px">
              <DoneAnimation />
            </Box>
            <Box j="center" w={12}>
              <H3 color="dark" fullWidth marginBottom="lg" align="center">
                <FormattedMessage id="backup.upload.success_message" defaultMessage="You're all set" />
              </H3>
              <Text fullWidth marginBottom="xl" size="sm" align="center">
                <FormattedMessage
                  id="backup.upload.success_blurb"
                  defaultMessage="All data from {fileName} has successfully been imported to {domain}. "
                  values={{ "fileName": fileName, "domain": website.domain.domain }}
                />
              </Text>
              <Button
                marginBottom="lg"
                onClick={() => { setModalOpen(false); setUploadState(UploadState.Processing); setUploading(false); setLogData("") }}
                variant="primary"
                size="small"
                backgroundColor="accent"
                backgroundColorHover="accentB10"
              >
                <FormattedMessage id="manage_website" defaultMessage="Manage website" />
              </Button>
            </Box>
          </CardBody>
        </Card>
      </Modal>
    )
  }

  if(uploading){
    return (
      <Modal>
        <Card>
          <CardBody>
            <Box j="center" mb="sm" mt="lg" px="lg">
              <Span align="center" weight="light" size="2xl" ellipsis fullWidth>
                <FormattedMessage id="backups.uploading_to_domain" defaultMessage="Importing to {domain}" values={{ "domain": website.domain.domain }} />
              </Span>
            </Box>
            <Box w="{12}" mb="xl" a="center">
              <Text align="center" fullWidth size="xs">
                {uploadProgress}%
              </Text>
            </Box>
            <Box wrap="nowrap" minW="100%" w={12}>
              <Box css={style.uploadProgressProgressBar(uploadState)}>
                <ProgressBar size="sm" percentageComplete={uploadProgress} />
              </Box>

          </Box>
          <Box d="row" mb="lg" a="center" px="md">
            <Icon name="info" color="info" marginRight="xs" />
            <Text size="sm">
              <FormattedMessage id="backups.upload_warning" defaultMessage="The speed of upload will depend on your internet connection.  " />
            </Text>
          </Box>

          <Box bg="grey5" overflow="auto" h="150px" p="lg" mt="sm" mb="lg" innerRef={logRef}>
            <Text size="sm" whiteSpace="normal" wordBreak="break-word">
              <pre style={{ marginTop: 0, whiteSpace: 'pre-line' }}>
                {logData}
              </pre>
            </Text>
          </Box>
          </CardBody>
          {uploadState === UploadState.Failed && (<CardFooter>
            <Box a="flex-end" j="flex-end">
              <Button variant="tertiary" size="small" onClick={() => setModalOpen(false)} marginRight="lg">
                <FormattedMessage id="close" defaultMessage="Close" />
              </Button>
            </Box>
          </CardFooter>)}
        </Card>
      </Modal>

    );
  }

     return (
      <Modal>
        <Card>
          <CardBody>
            <Box j="center" mb="xl" mt="lg" px="lg">
              <Span align="center" weight="light" size="2xl" ellipsis fullWidth>
                <FormattedMessage id="backups.upload_to_domain" defaultMessage="Upload backup to {domain}" values={{ "domain": website.domain.domain }} />
              </Span>
            </Box>
            <Box px="lg">
              <Text size="sm" marginBottom="lg">
                <FormattedMessage id="backups.upload_instructions" defaultMessage="Select an Enhance backup (tar.gz) to import all files, databases and emails to {domain}, overwriting any existing data. "  values={{ "domain": website.domain.domain }} />
              </Text>

              <input
                type="file"
                onChange={handleFileChange}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />

              <Button
                size="small"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                marginBottom="lg"
              >
                {uploading ? <FormattedMessage id="backups.uploading_progress" defaultMessage="Uploading backup {uploadProgress}%" values={{ uploadProgress }} /> : <FormattedMessage id="backups.choose_file" defaultMessage="Choose file" />}
              </Button>

              <Box d="row" mb="lg" a="center">
                <Icon name="warning-filled" color="warning" marginRight="xs" />
                <Text size="sm" weight="bold">
                  <FormattedMessage id="backups.upload.irrevocable" defaultMessage="This action is irrevocable.  The upload will begin as soon as your file is selected." />
                </Text>
              </Box>
            </Box>
          </CardBody>
          <CardFooter>
            <Box a="flex-end" j="flex-end">
              <Button variant="tertiary" size="small" onClick={() => setModalOpen(false)} marginRight="lg">
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
            </Box>
          </CardFooter>
        </Card>
      </Modal>
    )
  }


